import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { theme as defaultTheme } from '../theme';
import axios from 'axios';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [brandingColors, setBrandingColors] = useState({
    primary: defaultTheme.colors.primary,
    primaryDark: defaultTheme.colors.primaryDark,
    primaryLight: defaultTheme.colors.primaryLight,
  });

  // Fetch branding colors from backend
  useEffect(() => {
    const fetchBrandingColors = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const response = await axios.get(`${API_BASE_URL}/website/settings`);
        console.log('ThemeContext: Fetching branding colors from backend');
        console.log('ThemeContext: Response:', response.data);
        
        const settings = response.data?.settings || response.data;
        
        if (settings) {
          const colors = {};
          if (settings.primary_color) colors.primary = settings.primary_color;
          if (settings.primary_dark_color) colors.primaryDark = settings.primary_dark_color;
          if (settings.primary_light_color) colors.primaryLight = settings.primary_light_color;
          
          console.log('ThemeContext: Extracted colors:', colors);
          
          if (Object.keys(colors).length > 0) {
            setBrandingColors(prev => {
              console.log('ThemeContext: Updating branding colors from', prev, 'to', { ...prev, ...colors });
              return { ...prev, ...colors };
            });
          }
        }
      } catch (error) {
        console.warn('Failed to load branding colors, using defaults:', error);
      }
    };

    fetchBrandingColors();
    
    // Listen for settings updates
    const handleSettingsUpdate = (event) => {
      console.log('ThemeContext: Settings update event received', event.detail);
      fetchBrandingColors();
    };
    
    window.addEventListener('website-settings-updated', handleSettingsUpdate);
    
    return () => {
      window.removeEventListener('website-settings-updated', handleSettingsUpdate);
    };
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Create light and dark theme variants with dynamic branding colors
  const lightTheme = {
    ...defaultTheme,
    mode: 'light',
    colors: {
      ...defaultTheme.colors,
      primary: brandingColors.primary,
      primaryDark: brandingColors.primaryDark,
      primaryLight: brandingColors.primaryLight,
    }
  };

  const darkTheme = {
    ...defaultTheme,
    mode: 'dark',
    colors: {
      ...defaultTheme.colors,
      primary: brandingColors.primary,
      primaryDark: brandingColors.primaryDark,
      primaryLight: brandingColors.primaryLight,
      // Dark theme color overrides
      background: {
        primary: '#1F2937',
        secondary: '#374151',
        tertiary: '#4B5563'
      },
      text: {
        primary: '#F9FAFB',
        secondary: '#D1D5DB',
        tertiary: '#9CA3AF',
        inverse: '#1F2937'
      },
      border: {
        light: '#374151',
        medium: '#4B5563',
        dark: '#6B7280'
      }
    }
  };

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  const value = {
    theme: currentTheme,
    isDarkMode,
    toggleTheme,
    updateBrandingColors: setBrandingColors,
  };

  return (
    <ThemeContext.Provider value={value}>
      <StyledThemeProvider theme={currentTheme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    console.error('useTheme must be used within a ThemeProvider');
    // Return a fallback theme instead of throwing an error
    return {
      theme: defaultTheme,
      isDarkMode: false,
      toggleTheme: () => {}
    };
  }
  return context;
};

export default ThemeContext;
